import { MouseEvent, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@mui/material';

import { useShallow } from 'zustand/react/shallow';

import { ReactComponent as ShoppingCartIcon } from '@/assets/icons/menu-shopping-cart.svg';
import useGeneratikTableStore from '@/shared/ui/GeneratikTable/generatikTableStore';

import { ButtonIconWrapper, ButtonLabel, ButtonText, MenuButton } from '../SideMenu/styled';
import useSideMenuHoverPopover from '../SideMenu/useSideMenuHoverPopover';

interface ShoppingCartButtonProps {
  isExpanded: boolean;
  handleClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

const ShoppingCartButton = forwardRef<HTMLButtonElement, ShoppingCartButtonProps>(
  ({ handleClick, isExpanded }, ref) => {
    const { t } = useTranslation();
    const { handleMouseEnter, handleMouseLeave, hoverPopover, closePopover } =
      useSideMenuHoverPopover(t('Selected Spaces'), isExpanded);

    const selectedRowsCount = useGeneratikTableStore(
      useShallow((state) => {
        const spaces = state.selectedRows['ADVERTISING_SPACES'] ?? [];
        const packages = state.selectedRows['ADVERTISING_PACKAGES'] ?? [];

        return spaces.length + packages.length;
      }),
    );

    return (
      <>
        <MenuButton
          ref={ref}
          variant="text"
          isExpanded={isExpanded}
          data-testid="shopping-cart-button"
          onClick={(e) => {
            closePopover();
            handleClick(e);
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ overflow: 'visible' }}
        >
          <ButtonLabel>
            <ButtonIconWrapper isExpanded={isExpanded}>
              <ShoppingCartIcon />

              <ButtonText isExpanded={isExpanded}>{t('Selected Spaces')}</ButtonText>
            </ButtonIconWrapper>

            {selectedRowsCount > 0 && (
              <Badge
                badgeContent={selectedRowsCount}
                // @ts-ignore
                color="accent"
                sx={{
                  position: 'absolute',
                  transition: 'all 0.4s linear',
                  ...(isExpanded
                    ? { left: 'calc(100% - 4rem)', top: '1.25rem' }
                    : { left: '2rem', top: '0.5rem' }),

                  '& > span': {
                    transition: 'all 0.3s linear',
                    fontSize: '0.625rem!important',
                    height: 'auto',
                    padding: isExpanded ? '0.25rem 0.625rem' : '0.25rem',
                  },
                }}
              />
            )}
          </ButtonLabel>
        </MenuButton>

        {hoverPopover}
      </>
    );
  },
);

ShoppingCartButton.displayName = 'ShoppingCartButton';

export default ShoppingCartButton;
